/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Accessibility, Eye, Users, Zap, RotateCcw, Type } from 'lucide-react';
import { useAccessibility } from '@/lib/accessibility';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { SectionHeader, Row, TextScaleControl, MotionControl } from '@/components/accessibility/a11yParts';

const A11Y_ACCENT = '#7C3AED';

/** Row definitions: label keys + which control to render. Kept data-driven. */
const buildRows = (a11y, t) => [
  {
    testId: 'a11y-row-elderly', Icon: Users,
    title: t('a11yElderlyTitle', 'Modalità Anziani'),
    description: t('a11yElderlyDesc', 'Testo più grande e leggibile, pulsanti giganti, animazioni ridotte per una migliore esperienza.'),
    control: <Switch checked={a11y.elderlyMode} onCheckedChange={a11y.setElderlyMode} data-testid="a11y-elderly-toggle" aria-label={t('a11yElderlyTitle', 'Modalità Anziani')} />,
  },
  {
    testId: 'a11y-row-contrast', Icon: Eye,
    title: t('a11yContrastTitle', 'Contrasto elevato'),
    description: t('a11yContrastDesc', 'Contrasto AAA per una migliore leggibilità con problemi di vista.'),
    control: <Switch checked={a11y.highContrast} onCheckedChange={a11y.setHighContrast} data-testid="a11y-contrast-toggle" aria-label={t('a11yContrastTitle', 'Contrasto elevato')} />,
  },
  {
    testId: 'a11y-row-scale', Icon: Type,
    title: t('a11yScaleTitle', 'Dimensione testo'),
    description: t('a11yScaleDesc', 'Aumenta la grandezza dei caratteri in tutta l\'app.'),
    control: <TextScaleControl value={a11y.textScale} onChange={a11y.setTextScale} />,
  },
  {
    testId: 'a11y-row-motion', Icon: Zap,
    title: t('a11yMotionTitle', 'Riduci animazioni'),
    description: t('a11yMotionDesc', 'Riduce transizioni e movimenti per chi soffre di vertigini o disturbi vestibolari.'),
    control: <MotionControl value={a11y.reduceMotion} onChange={a11y.setReduceMotion} />,
  },
];

/**
 * Accessibility Card for Settings page.
 * Exposes: elderly mode, high contrast, reduce motion, text scale.
 */
export const AccessibilityCard = () => {
  const { t } = useTranslation();
  const a11y = useAccessibility();
  const rows = buildRows(a11y, t);

  return (
    <div className="rounded-2xl bg-card border border-border p-5" data-testid="settings-a11y-card">
      <SectionHeader
        Icon={Accessibility}
        title={t('a11yTitle', 'Accessibilità')}
        subtitle={t('a11ySubtitle', 'Personalizza la lettura e l\'uso dell\'app')}
        color={A11Y_ACCENT}
      />
      <div className="mt-2">
        {rows.map((r) => <Row key={r.testId} {...r} />)}
      </div>
      <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground max-w-[420px]">
          {t('a11yNote', 'Le preferenze di accessibilità vengono salvate sul dispositivo e applicate immediatamente.')}
        </p>
        <Button type="button" variant="ghost" size="sm" onClick={a11y.resetAll} data-testid="a11y-reset-btn" className="gap-1">
          <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
          {t('a11yReset', 'Ripristina predefiniti')}
        </Button>
      </div>
    </div>
  );
};

export default AccessibilityCard;
